import React, { useState, useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Spinner, Message } from '../../../components';
import { useTranslation } from 'react-i18next';
import dynamicAPI from '../../../api/dynamicAPI';
import useTranslationsAPI from '../../../api/translations';
import KeyValueEditor from '../../../utils/KeyValueEditor';
import "../../../css/translationsEditor.css";

const TranslationsEditor = () => {
    const { t, i18n } = useTranslation();
    const defaultLang = i18n.language || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLang);
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [showRaw, setShowRaw] = useState(false);
    const [rawText, setRawText] = useState('');
    const [entryList, setEntryList] = useState([]);

    const { translationsQuery, updateMutation, generateMutation } = useTranslationsAPI(selectedLanguage);
    const { data, isLoading, isError, error } = translationsQuery;

    useEffect(() => {
        const fetchAllLanguages = async () => {
            try {
                const response = await dynamicAPI('post', '/api/auth/translations', {});
                if (response.translations) {
                    const langs = response.translations.map(item => item.language);
                    setAvailableLanguages(langs);
                }
            } catch (err) {
                console.error('Error fetching available languages:', err);
            }
        };
        fetchAllLanguages();
    }, []);

    useEffect(() => {
        if (data) {
            let extracted = {};
            if (data.langFound === false) {
                extracted = {};
            } else if (data.langFound === true && data.translation) {
                extracted = data.translation.data?.entries || {};
            } else if (data.translations) {
                const found = data.translations.find(item => item.language === selectedLanguage);
                extracted = found?.data?.entries || {};
            }

            const asList = Object.entries(extracted).map(([key, value]) => ({ key, value }));
            setEntryList(asList);
            setRawText(JSON.stringify(extracted, null, 2));
        }
    }, [data, selectedLanguage]);

    const handleEditorChange = (updatedEntries) => {
        setEntryList(updatedEntries);

        const obj = updatedEntries.reduce((acc, { key, value }) => {
            if (key.trim()) acc[key] = value;
            return acc;
        }, {});
        setRawText(JSON.stringify(obj, null, 2));
    };

    const handleRawChange = (e) => {
        setRawText(e.target.value);
    };

    const parseRaw = () => {
        try {
            const parsed = JSON.parse(rawText);
            const entries = Object.entries(parsed).map(([key, value]) => ({ key, value }));
            setEntryList(entries);
            setShowRaw(false);
        } catch (err) {
            alert(t('Invalid JSON'));
        }
    };

    const handleSave = () => {
        const entriesObj = entryList.reduce((acc, { key, value }) => {
            if (key.trim()) acc[key] = value;
            return acc;
        }, {});

        updateMutation.mutate(
            { lang: selectedLanguage, entries: entriesObj },
            { onSuccess: () => translationsQuery.refetch() }
        );

        if (!availableLanguages.includes(selectedLanguage)) {
            setAvailableLanguages([...availableLanguages, selectedLanguage]);
        }

        setRawText(JSON.stringify(entriesObj, null, 2));
    };

    const handleSortEntries = () => {
        const sorted = [...entryList].sort((a, b) => a.key.localeCompare(b.key));
        setEntryList(sorted);

        const obj = sorted.reduce((acc, { key, value }) => {
            if (key.trim()) acc[key] = value;
            return acc;
        }, {});
        setRawText(JSON.stringify(obj, null, 2));
    };

    const handleLanguageSelect = (lang) => {
        setSelectedLanguage(lang);
    };

    const handleAddNewLanguage = () => {
        const newLang = prompt(t('Enter new language code (e.g., el, gr, it, us):'));
        if (newLang) {
            setSelectedLanguage(newLang);
            if (!availableLanguages.includes(newLang)) {
                setAvailableLanguages([...availableLanguages, newLang]);
            }
            setEntryList([]);
            setRawText('{}');
        }
    };

    const handleGenerateTranslations = () => {
        if (!window.confirm(t('Are you sure you want to regenerate translations?'))) {
            return;
        }
        generateMutation.mutate();
    };

    let lastGeneratedDisplay = '';
    if (data?.langFound && data.translation?.lastGenerated) {
        lastGeneratedDisplay = new Date(data.translation.lastGenerated).toLocaleString();
    }
    let lastEditedDisplay = '';
    if (data?.langFound && data.translation?.updatedAt) {
        lastEditedDisplay = new Date(data.translation.updatedAt).toLocaleString();
    }
    return (
        <>
            <HelmetProvider>
                <Helmet>
                    <title>{t('Edit Translations')}</title>
                    <meta property="og:title" content={t('Edit Translations')} />
                </Helmet>
            </HelmetProvider>
            <div className="translations-editor container">
                <h2 className="text-center mb-3">{t('Edit Translations')}</h2>

                <div style={{ borderBottom: '1px solid gray' }} className="mb-3">
                    <h4>{t('Available Languages')}:</h4>
                    {availableLanguages.map((lang) => (
                        <button
                            key={lang}
                            className={`btn ${selectedLanguage === lang ? 'btn-primary' : 'btn-secondary'} mx-1`}
                            onClick={() => handleLanguageSelect(lang)}
                        >
                            {lang.toUpperCase()}
                        </button>
                    ))}
                    <button className="btn btn-success mx-1" onClick={handleAddNewLanguage}>
                        {t('Add New Language')}
                    </button>
                    <button className="btn btn-warning float-right" onClick={handleGenerateTranslations}>
                        {t('Generate Translations')}
                    </button>

                    {lastGeneratedDisplay && (
                        <div className="float-left mr-3" style={{ fontSize: '0.9rem', paddingTop: '8px' }}>
                            {t('Last generated')}: {lastGeneratedDisplay}
                            {lastEditedDisplay && <div>{t('Last edited')}: {lastEditedDisplay}</div>}
                        </div>
                    )}
                </div>

                {isLoading ? (
                    <Spinner />
                ) : isError ? (
                    <Message variant="danger">
                        {error?.message || t('Error loading translations')}
                    </Message>
                ) : (
                    <>
                        <div className="mb-3 text-end">
                            <button className="btn btn-secondary" onClick={() => setShowRaw(!showRaw)}>
                                {showRaw ? t('Switch to Key-Value Editor') : t('Switch to Raw Editor')}
                            </button>
                            <button className="btn btn-info mx-1" onClick={handleSortEntries}>
                                {t('Sort Entries')}
                            </button>
                            <button className="btn btn-primary" onClick={handleSave}>
                                {t('Save Changes')}
                            </button>
                        </div>

                        {showRaw ? (
                            <div className="mb-3">
                                <textarea
                                    rows="10"
                                    className="form-control"
                                    value={rawText}
                                    onChange={handleRawChange}
                                />
                                <button className="btn btn-info mt-2" onClick={parseRaw}>
                                    {t('Parse Raw JSON')}
                                </button>
                            </div>
                        ) : (
                            <div className="editor-container mb-3">
                                <KeyValueEditor
                                    t={t}
                                    entries={entryList}
                                    onChange={handleEditorChange}
                                />
                            </div>
                        )}
                        <div className="text-end">
                            <button className="btn btn-primary" onClick={handleSave}>
                                {t('Save Changes')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default TranslationsEditor;
